import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button, Card, Col, Empty, Result, Row, Skeleton, Tag, theme } from "antd";
import {
  ApartmentOutlined,
  FolderOpenOutlined,
  IdcardOutlined,
  RiseOutlined,
  ShopOutlined,
  TeamOutlined
} from "@ant-design/icons";
import ReactApexChart from "react-apexcharts";
import { useTranslation } from "react-i18next";
import ResponsiveTable from "../../../design-system/ResponsiveTable";
import "./AdminDashboard.css";

// Tag colors for the project status enum (antd semantic presets, no hardcoded colors).
const STATUS_TAG = {
  "JUST ADDED": "default",
  "IN PROGRESS": "processing",
  REVISION: "purple",
  ISSUES: "warning",
  COMPLETED: "success",
  REJECTED: "error"
};

const spName = sp =>
  sp?.companyName || [sp?.firstName, sp?.lastName].filter(Boolean).join(" ") || "—";

// Super-admin home page (/admin). The shell (PanelsHeader) renders the page title.
const AdminDashboard = () => {
  const { t, i18n } = useTranslation("dashboard");
  const { token } = theme.useToken();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/dashboard/admin-summary`, {
        headers: { Authorization: "Bearer " + localStorage.getItem("token") }
      });
      setData(res.data?.data || null);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const formatDate = value => {
    if (!value) return "—";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "—" : date.toLocaleDateString(i18n.language);
  };
  const statusLabel = status => t(status === "OTHER" ? "Other" : status);

  if (failed && !data) {
    return (
      <div className="admin-dashboard">
        <Card className="admin-dashboard__card">
          <Result
            status="warning"
            title={t("Could not load the dashboard.")}
            extra={
              <Button type="primary" onClick={load} loading={loading}>
                {t("Try again")}
              </Button>
            }
          />
        </Card>
      </div>
    );
  }

  const sp = data?.serviceProviders;
  const kpis = [
    {
      key: "serviceProviders",
      label: t("Service Providers"),
      value: sp?.total,
      detail: sp && t("{{active}} active, {{blocked}} blocked", sp),
      icon: <ShopOutlined />,
      color: token.colorPrimary,
      to: "/admin/users-list"
    },
    {
      key: "newThisMonth",
      label: t("New this month"),
      value: sp?.newThisMonth,
      detail: t("Service Providers"),
      icon: <RiseOutlined />,
      color: token.colorSuccess,
      to: "/admin/users-list"
    },
    {
      key: "subadmins",
      label: t("Subadmins"),
      value: data?.subadmins?.total,
      icon: <ApartmentOutlined />,
      color: token.colorInfo,
      to: "/admin/subadmins"
    },
    {
      key: "projects",
      label: t("Projects"),
      value: data?.projects?.total,
      icon: <FolderOpenOutlined />,
      color: token.colorWarning,
      to: "/admin/projects"
    },
    {
      key: "employees",
      label: t("Employees"),
      value: data?.employees?.total,
      detail: data?.employees && t("{{count}} active", { count: data.employees.active }),
      icon: <IdcardOutlined />,
      color: token.colorPrimary
    },
    {
      key: "clients",
      label: t("Clients"),
      value: data?.clients?.total,
      icon: <TeamOutlined />,
      color: token.colorPrimary
    }
  ];

  const byStatus = data?.projects?.byStatus || [];
  const chartOptions = {
    chart: {
      type: "bar",
      toolbar: { show: false },
      fontFamily: token.fontFamily,
      foreColor: token.colorTextSecondary,
      animations: { enabled: false }
    },
    plotOptions: {
      bar: {
        horizontal: true,
        borderRadius: 4,
        borderRadiusApplication: "end",
        barHeight: "60%",
        // Count sits just past the bar end, in text ink rather than the bar color.
        dataLabels: { position: "top" }
      }
    },
    colors: [token.colorPrimary],
    dataLabels: {
      enabled: true,
      style: { colors: [token.colorText], fontWeight: 600 },
      offsetX: 20
    },
    xaxis: {
      categories: byStatus.map(row => statusLabel(row.status)),
      labels: { formatter: value => (Number.isInteger(Number(value)) ? value : "") }
    },
    yaxis: { labels: { maxWidth: 140 } },
    grid: { borderColor: token.colorBorderSecondary, strokeDashArray: 2 },
    tooltip: { y: { title: { formatter: () => t("Projects") } } },
    states: { hover: { filter: { type: "darken", value: 0.9 } } }
  };
  const chartSeries = [{ name: t("Projects"), data: byStatus.map(row => row.count) }];

  const spColumns = [
    {
      title: t("Company Name"),
      key: "name",
      render: (_, record) => spName(record),
      mobile: "title"
    },
    { title: t("Email"), dataIndex: "email", key: "email", mobile: "subtitle" },
    {
      title: t("Status"),
      dataIndex: "status",
      key: "status",
      render: status => (
        <Tag color={status === "Blocked" ? "error" : "success"}>
          {t(status === "Blocked" ? "Blocked" : "Active")}
        </Tag>
      )
    },
    { title: t("Created At"), dataIndex: "createdAt", key: "createdAt", render: formatDate }
  ];

  const projectColumns = [
    { title: t("Project Name"), dataIndex: "projectName", key: "projectName", mobile: "title" },
    {
      title: t("Service Provider"),
      dataIndex: "serviceProvider",
      key: "serviceProvider",
      render: value => spName(value),
      mobile: "subtitle"
    },
    {
      title: t("Status"),
      dataIndex: "status",
      key: "status",
      render: status => (
        <Tag color={STATUS_TAG[status] || "default"}>{status ? t(status) : t("Other")}</Tag>
      )
    },
    { title: t("Created At"), dataIndex: "createdAt", key: "createdAt", render: formatDate }
  ];

  return (
    <div className="admin-dashboard">
      <Row gutter={[16, 16]}>
        {kpis.map(kpi => {
          const tile = (
            <Card className="admin-dashboard__card admin-dashboard__kpi" hoverable={!!kpi.to}>
              {loading && !data ? (
                <Skeleton active title={false} paragraph={{ rows: 2 }} />
              ) : (
                <>
                  <div className="admin-dashboard__kpi-head">
                    <span className="admin-dashboard__kpi-label">{kpi.label}</span>
                    <span
                      className="admin-dashboard__kpi-icon"
                      style={{ color: kpi.color, background: token.colorFillQuaternary }}
                      aria-hidden="true"
                    >
                      {kpi.icon}
                    </span>
                  </div>
                  <div className="admin-dashboard__kpi-value">{kpi.value ?? 0}</div>
                  {kpi.detail && <div className="admin-dashboard__muted">{kpi.detail}</div>}
                </>
              )}
            </Card>
          );
          return (
            <Col xs={12} md={8} lg={6} key={kpi.key}>
              {kpi.to ? (
                <Link to={kpi.to} className="admin-dashboard__kpi-link">
                  {tile}
                </Link>
              ) : (
                tile
              )}
            </Col>
          );
        })}
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={10}>
          <Card title={t("Projects by status")} className="admin-dashboard__card">
            {loading && !data ? (
              <Skeleton active paragraph={{ rows: 6 }} />
            ) : data?.projects?.total ? (
              <div className="admin-dashboard__chart">
                <ReactApexChart
                  options={chartOptions}
                  series={chartSeries}
                  type="bar"
                  height={Math.max(220, byStatus.length * 40)}
                  width="100%"
                />
              </div>
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("No projects yet")} />
            )}
          </Card>
        </Col>
        <Col xs={24} lg={14}>
          <Card
            title={t("Recent service providers")}
            className="admin-dashboard__card"
            extra={<Link to="/admin/users-list">{t("View All")}</Link>}
          >
            <ResponsiveTable
              rowKey="_id"
              columns={spColumns}
              dataSource={data?.recentServiceProviders || []}
              loading={loading}
              pagination={false}
              size="middle"
              locale={{ emptyText: t("No service providers yet") }}
            />
          </Card>
        </Col>
      </Row>

      <Card
        title={t("Recent projects")}
        className="admin-dashboard__card"
        extra={<Link to="/admin/projects">{t("View All")}</Link>}
      >
        <ResponsiveTable
          rowKey="_id"
          columns={projectColumns}
          dataSource={data?.recentProjects || []}
          loading={loading}
          pagination={false}
          size="middle"
          locale={{ emptyText: t("No projects yet") }}
        />
      </Card>
    </div>
  );
};

export default AdminDashboard;
